import { TranslationKey } from '@Localization';
import { inject, injectable, interfaces } from 'inversify';
import _ from 'lodash';
import {
  ImportAnnotationsFinished,
  ImportAnnotationsFinishedType,
} from '../../../../../modules/projects/details/images/importAnnotations/events/ImportAnnotationsFinished';
import { ACDLError, ForbiddenError } from '../../../../models/error.model';
import { EventListeningDisposer, IEventBus } from '../../../../services/eventBus.service';
import { cleanClones, validateQuestion } from '../../../annotation/question.model';
import { NotificationLevel, ToastNotification } from '../../../notifications/models/notification.model';
import { INotificationsService } from '../../../notifications/services/notifications.service';
import { ICanPublishProjectResponse } from '../../projectDetails.models';
import { IProjectDetailsSubmodule } from '../../services/IProjectDetailsSubmodule';
import { ImageSetType } from './projectDetailsImages.model';
import { IProjectDetailsImagesStore } from './projectDetailsImages.store';
import { IImageFilterService } from './services/imageFilters.service';
import { IProjectDetailsImagesApiService } from './services/projectDetailsImagesApi.service';

export interface IProjectDetailsImagesBl extends IProjectDetailsSubmodule {
  // images
  getImageTabDataAsync(projectId: string, filterId: string | undefined): Promise<void>;
  getProjectImagesAsync(): Promise<void>;

  // batch answer
  toggleAnswerQuestionsModal(): void;
  batchAnswerQuestionsAsync(): Promise<void>;
  updateBatchAnswerQuestionsProgress(failed: boolean, totalBatchesCount: number): void;
  batchAnswerQuestionsRequestFinishedAsync(): Promise<void>;

  // sets
  updateProjectImageSetAsync(imageId: string, setType: ImageSetType): Promise<void | ACDLError>;
  updateBatchProjectImageSetAsync(setType: ImageSetType): Promise<void | ACDLError>;

  setToggledImages(selectedImages: string[], lastSelectedId: string | undefined): void;
}

export namespace IProjectDetailsImagesBl {
  export const $: interfaces.ServiceIdentifier<IProjectDetailsImagesBl> = Symbol('PROJECT_DETAILS_IMAGES_SERVICE');
}

@injectable()
export class ProjectDetailsImagesBl implements IProjectDetailsImagesBl {
  private importAnnotationsFinishedDisposer?: EventListeningDisposer;

  constructor(
    @inject(IProjectDetailsImagesStore.$) private readonly store: IProjectDetailsImagesStore,
    @inject(IProjectDetailsImagesApiService.$) private readonly projectDetailsImagesApiService: IProjectDetailsImagesApiService,
    @inject(INotificationsService.$) private readonly notificationsService: INotificationsService,
    @inject(IImageFilterService.$) private readonly imageFilterService: IImageFilterService,
    @inject(IEventBus.$) private readonly eventBus: IEventBus
  ) {}

  handleImportAnnotationsFinishedAsync = async (event: ImportAnnotationsFinished): Promise<void> => {
    if (this.store.projectId !== event.projectId) {
      return;
    }
    await this.getProjectImagesAsync();
  };

  initialize(): void {
    this.importAnnotationsFinishedDisposer = this.eventBus.addListener(this.handleImportAnnotationsFinishedAsync, ImportAnnotationsFinishedType);
    this.store.resetStorage('', undefined);
  }

  cleanup(): void {
    this.importAnnotationsFinishedDisposer?.();
    this.store.resetStorage('', undefined);
  }

  validate(): void {
    // nop
  }

  getValidationErrors(): string[] {
    return [];
  }

  handleCanPublishCheck(_: ICanPublishProjectResponse): void {
    // nop
  }

  async getImageTabDataAsync(projectId: string, filterId: string | undefined): Promise<void> {
    this.store.resetStorage(projectId, filterId);

    const [questionsResult] = await Promise.all([
      this.projectDetailsImagesApiService.getQuestions({ projectId }),
      this.imageFilterService.getFilterDataAsync(projectId, filterId),
    ]);

    if (questionsResult instanceof ACDLError) {
      return;
    }

    this.store.setQuestions(cleanClones(questionsResult));
  }

  async getProjectImagesAsync(): Promise<void> {
    await this.imageFilterService.filterImages();
  }

  setToggledImages(toggledImages: string[], lastSelectedId: string | undefined): void {
    this.store.setToggledImages(toggledImages, lastSelectedId);
  }

  toggleAnswerQuestionsModal(): void {
    this.store.resetBatchAnswerQuestions();
  }

  async batchAnswerQuestionsAsync(): Promise<void> {
    let isValid = true;

    _.each(this.store.questions, (q) => {
      if (!validateQuestion(q)) {
        isValid = false;
      }
    });

    if (!isValid || !this.store.batchAnswerQuestionModalSessionId) {
      return;
    }

    this.store.setBatchAnswerQuestionsRequestInProgress(true);

    const result = await this.projectDetailsImagesApiService.batchAnswerQuestionsAsync({
      projectId: this.store.projectId,
      filterId: this.store.filterId,
      toggledImages: this.store.toggledImages.slice(),
      selectionMode: this.store.selectionMode,
      questions: this.store.questions.slice(),
      sessionId: this.store.batchAnswerQuestionModalSessionId,
    });

    if (result instanceof ACDLError) {
      let errorCode = undefined;

      if (result.apiErrorResponse && result.apiErrorResponse.errorCodes.length > 0) {
        errorCode = result.apiErrorResponse.errorCodes[0];
      }

      this.store.setBatchAnswerQuestionsRequestInProgress(false, errorCode);
    }
  }

  updateBatchAnswerQuestionsProgress(failed: boolean, totalBatchesCount: number): void {
    const { batchAnswerFailedBatches, batchAnswerSuccessfulBatches } = this.store;

    if (failed) {
      this.store.setBatchAnswerQuestionsProgress(totalBatchesCount, batchAnswerFailedBatches + 1, batchAnswerSuccessfulBatches);
    } else {
      this.store.setBatchAnswerQuestionsProgress(totalBatchesCount, batchAnswerFailedBatches, batchAnswerSuccessfulBatches + 1);
    }
  }

  async batchAnswerQuestionsRequestFinishedAsync(): Promise<void> {
    this.store.setSelectionMode('Select');
    await this.getProjectImagesAsync();
  }

  async updateProjectImageSetAsync(imageId: string, setType: ImageSetType): Promise<void | ACDLError> {
    const currentImageSetType = this.store.detailsImages.find((i) => i.id === imageId)?.imageSet;
    this.store.updateImageSetType(imageId, setType);

    const result = await this.projectDetailsImagesApiService.updateProjectImageSetAsync({
      projectId: this.store.projectId,
      filterId: '',
      toggledImages: [imageId],
      selectionMode: 'Deselect',
      projectImageSet: setType,
    });

    await this.notifyAboutApiError(result, 'updating_project_images_set_failed');
    if (result instanceof ACDLError && currentImageSetType) {
      this.store.updateImageSetType(imageId, currentImageSetType);
      return;
    }
  }

  async updateBatchProjectImageSetAsync(setType: ImageSetType): Promise<void | ACDLError> {
    const result = await this.projectDetailsImagesApiService.updateProjectImageSetAsync({
      projectId: this.store.projectId,
      filterId: this.store.filterId,
      toggledImages: this.store.toggledImages.slice(),
      selectionMode: this.store.selectionMode,
      projectImageSet: setType,
    });

    await this.notifyAboutApiError(result, 'updating_project_images_set_failed');
    if (result instanceof ACDLError) {
      return;
    }

    await this.getProjectImagesAsync();
  }

  async notifyAboutApiError(result: unknown, errorCode: TranslationKey, dontShowBadRequest: boolean = false): Promise<void> {
    if (result instanceof ACDLError && !(result instanceof ForbiddenError) && (!dontShowBadRequest || !result.isBadRequest())) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, errorCode));
    }
  }
}
